import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Card, CardContent } from '../../../../components/ui/card.js';
import type { WorkfrontCustomFormGroup, WorkfrontCustomFormModel } from '../types.js';
import { CustomFormGroupParam } from './CustomFormGroupParam.js';

interface CustomFormDetailsProps {
  customLeftFormDetails: WorkfrontCustomFormModel | null;
  customRightFormDetails: WorkfrontCustomFormModel | null;
}

function CollapsibleGroup({ group }: { group: WorkfrontCustomFormGroup }): JSX.Element {
  const [open, setOpen] = useState(true);

  return (
    <Card className="border-amber-400/30 bg-foreground/5 shadow-none">
      <CardContent className="p-3">
        <button
          type="button"
          className="flex w-full items-center gap-1.5 text-left text-[1.05rem] font-bold text-sky-800 dark:text-sky-200"
          onClick={() => setOpen((prev) => !prev)}
        >
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" />
          )}
          {group.name}
        </button>
        {open && (
          <div className="mt-1">
            {group.params.map((parameter) => (
              <CustomFormGroupParam key={parameter.id} parameter={parameter} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FormData({ data }: { data: WorkfrontCustomFormModel }): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-1 text-foreground/85">
      <h4 className="m-0 text-lg font-bold">{data.name}</h4>
      {data.description && <p className="my-1 text-foreground/55">{data.description}</p>}
      <p className="mb-2 text-sm">
        <b>objTypes:</b> {data.objTypes.join(', ')}
      </p>

      <div className="space-y-3">
        {data.groups.map((group) => (
          <CollapsibleGroup key={group.id} group={group} />
        ))}
      </div>
    </div>
  );
}

/**
 * Collapsible form-structure preview used by both the Clone wizard
 * (single-panel — only source) and the Replace wizard (dual-panel —
 * source + target side by side). Verbatim port of
 * `wf-audit/.../CustomFormDetails.tsx` with the colour scheme adapted
 * to the dark customer-app theme.
 */
export function CustomFormDetails({
  customLeftFormDetails,
  customRightFormDetails,
}: CustomFormDetailsProps): JSX.Element | null {
  if (!customLeftFormDetails && !customRightFormDetails) {
    return null;
  }

  const isSinglePanel = !customLeftFormDetails || !customRightFormDetails;

  return (
    <Card className="border-sky-400/30 bg-foreground/5 shadow-none">
      <CardContent className="p-2">
        {isSinglePanel ? (
          <div className="flex min-w-0 flex-col gap-1.5 p-2.5">
            <FormData data={(customLeftFormDetails || customRightFormDetails)!} />
          </div>
        ) : (
          <div className="grid grid-cols-[minmax(0,1fr)_130px_minmax(0,1fr)] items-stretch max-lg:grid-cols-1 max-lg:gap-y-2">
            <div className="flex min-w-0 flex-col gap-1.5 p-2.5 max-lg:p-0">
              <FormData data={customLeftFormDetails!} />
            </div>
            <div className="pointer-events-none flex items-center justify-center max-lg:hidden">
              <div className="h-full border-l border-dashed border-foreground/15" />
            </div>
            <div className="flex min-w-0 flex-col gap-1.5 p-2.5 max-lg:p-0">
              <FormData data={customRightFormDetails!} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
